/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import { deriveEthWallet, deriveSolWallet } from '../utils/wallet';

async function deriveAccount( mnemonic, index ) {
    const accountMnemonic = index === 0 ? mnemonic : mnemonic + ' account' + index;
    const ethWallet = await deriveEthWallet( accountMnemonic );
    const solWallet = await deriveSolWallet( accountMnemonic );
    return {
        index,
        label:           'Account ' + ( index + 1 ),
        ethereumAddress: ethWallet.address,
        privateKey:      ethWallet.privateKey,
        solanaAddress:   solWallet.address,
        solanaSecretKey: Array.from( solWallet.secretKey ),
    };
}

export default function RosieChat( { fullPage = false } ) {
    const settings  = window.chainvaultSettings || {};
    const enabled   = settings.rosieEnabled  || false;
    const rosieName = settings.rosieName     || 'Rosie';
    const voiceOn      = settings.rosieVoice       || false;
    const elKey        = settings.rosieElKey       || '';
    const elVoice      = settings.rosieElVoice     || '21m00Tcm4TlvDq8ikWAM';
    const oaiKey       = settings.rosieOaiKey      || '';
    const oaiVoice     = settings.rosieOaiVoice    || 'nova';
    const ttsProvider  = settings.rosieTtsProvider || 'browser';

    const { address, selectedNetwork, balances, switchNetwork, switchAccount, sendFromBuiltIn, builtInWallet, currentNetwork, walletType, fetchSolanaBalance, fetchAllEvmBalances, showOnboarding, handleBuiltInWalletCreated } = useWallet();

    const [ open,      setOpen      ] = useState( false );
    const [ messages,  setMessages  ] = useState( () => {
        try {
            const saved = sessionStorage.getItem('rosie_messages');
            if (saved) return JSON.parse(saved);
        } catch(e) {}
        return [{ role: 'assistant', content: `Hi! I\'m ${ rosieName } 👋 I\'m your personal ChainVault assistant. Tap the mic to talk to me or type below. What can I help you with?` }];
    } );
    const [ input,     setInput     ] = useState( '' );
    const [ loading,   setLoading   ] = useState( false );
    const [ unread,    setUnread    ] = useState( 0 );
    const [ listening, setListening ] = useState( false );
    const [ speaking,  setSpeaking  ] = useState( false );
    const [ interimText, setInterimText ] = useState( '' );

    /* pending send flow */
    const [ pendingSend,    setPendingSend    ] = useState( null );
    const [ contactFlow,    setContactFlow    ] = useState( null ); /* {step, name, email} */
    const contactFlowRef = React.useRef( null );
    const [ awaitingSeedVerification, setAwaitingSeedVerification ] = useState( false );
    const awaitingSeedRef = React.useRef( false );
    const [ onboardingMnemonic,    setOnboardingMnemonic    ] = useState( null );
    const [ awaitingCopyConfirm,   setAwaitingCopyConfirm   ] = useState( false );
    const awaitingCopyRef = React.useRef( false );
    const [ awaitingRestore,       setAwaitingRestore       ] = useState( false );
    const [ collectedWords,        setCollectedWords        ] = useState( [] );
    const awaitingRestoreRef = React.useRef( false );
    const collectedWordsRef  = React.useRef( [] );
    const [ userName, setUserName ] = useState( () => {
        try { return sessionStorage.getItem('rosie_username') || null; } catch(e) { return null; }
    });
    const userNameRef = React.useRef( null );
    const pendingSendRef  = React.useRef( null );

    const bottomRef  = useRef( null );
    const inputRef   = useRef( null );
    const recognRef      = useRef( null );
    const seedTimerRef1  = useRef( null ); /* first 10s warning */
    const seedTimerRef2  = useRef( null ); /* second 10s warning */
    const seedTimerRef3  = useRef( null ); /* final 10s cancel */
    const audioRef   = useRef( null );

    /* ── Mic permission ── */
    const [ micGranted, setMicGranted ] = React.useState( false );

    React.useEffect( () => {
        const requestMic = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia( { audio: true } );
                stream.getTracks().forEach( t => t.stop() );
                setMicGranted( true );
            } catch( e ) {
                /* user said no — fine, they can still type */
                setMicGranted( false );
                console.log( 'Mic permission denied on load:', e.message );
            }
        };
        requestMic();
    }, [] );

    const hasWelcomed = useRef( false );
    const cursorTimer = useRef( null );

    useEffect( () => {
        if ( bottomRef.current ) bottomRef.current.scrollIntoView( { behavior: 'smooth' } );
        /* persist messages to sessionStorage so they survive navigation */
        try { sessionStorage.setItem('rosie_messages', JSON.stringify(messages)); } catch(e) {}
    }, [ messages, interimText ] );

    useEffect( () => {
        if ( open ) {
            setUnread( 0 );
            setTimeout( () => inputRef.current && inputRef.current.focus(), 100 );
        }
    }, [ open ] );

    /* ── Auto-welcome on cursor movement ── */
    useEffect( () => {
        if ( !enabled || hasWelcomed.current ) return;
        const handleMouseMove = () => {
            if ( hasWelcomed.current ) return;
            if ( cursorTimer.current ) clearTimeout( cursorTimer.current );
            cursorTimer.current = setTimeout( () => {
                if ( !hasWelcomed.current && !open ) {
                    hasWelcomed.current = true;
                    setOpen( true );
                    setUnread( 0 );
                    const appName = window.chainvaultSettings?.branding?.app_name || 'ChainVault';
                    const welcomeMsg = `Welcome to ${ appName }! 👋 I'm ${ rosieName }, your personal crypto assistant. Tap the 🎤 mic button to talk to me directly, or type your question below. How can I help you today?`;
                    setMessages( [ { role: 'assistant', content: welcomeMsg } ] );
                    /* speak removed — welcome message is silent */
                }
            }, 5000 );
        };
        window.addEventListener( 'mousemove', handleMouseMove );
        return () => {
            window.removeEventListener( 'mousemove', handleMouseMove );
            if ( cursorTimer.current ) clearTimeout( cursorTimer.current );
        };
    }, [ enabled, open ] );

    /* ── Activation chime ── */
    const playChime = ( type ) => {
        try {
            const ctx  = new ( window.AudioContext || window.webkitAudioContext )();
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect( gain );
            gain.connect( ctx.destination );
            if ( type === 'start' ) {
                osc.frequency.setValueAtTime( 880, ctx.currentTime );
                osc.frequency.setValueAtTime( 1100, ctx.currentTime + 0.1 );
                gain.gain.setValueAtTime( 0.3, ctx.currentTime );
                gain.gain.exponentialRampToValueAtTime( 0.001, ctx.currentTime + 0.25 );
                osc.start( ctx.currentTime );
                osc.stop( ctx.currentTime + 0.25 );
            } else {
                osc.frequency.setValueAtTime( 1100, ctx.currentTime );
                osc.frequency.setValueAtTime( 700, ctx.currentTime + 0.1 );
                gain.gain.setValueAtTime( 0.2, ctx.currentTime );
                gain.gain.exponentialRampToValueAtTime( 0.001, ctx.currentTime + 0.2 );
                osc.start( ctx.currentTime );
                osc.stop( ctx.currentTime + 0.2 );
            }
        } catch(e) {}
    };

    /* ── ElevenLabs TTS ── */
    const speakElevenLabs = useCallback( async ( text ) => {
        if ( !voiceOn || !elKey ) return;
        setSpeaking( true );
        try {
            const res = await fetch( `https://api.elevenlabs.io/v1/text-to-speech/${ elVoice }`, {
                method:  'POST',
                headers: { 'xi-api-key': elKey, 'Content-Type': 'application/json', 'Accept': 'audio/mpeg' },
                body: JSON.stringify( {
                    text,
                    model_id: 'eleven_turbo_v2_5',
                    voice_settings: { stability: 0.4, similarity_boost: 0.8, style: 0.5, use_speaker_boost: true },
                } ),
            } );
            if ( !res.ok ) throw new Error( 'ElevenLabs error' );
            const blob = await res.blob();
            const url  = URL.createObjectURL( blob );
            if ( audioRef.current ) { audioRef.current.pause(); }
            const audio = new Audio( url );
            audioRef.current = audio;
            audio.onended = () => { setSpeaking( false ); playChime( 'stop' ); };
            playChime( 'start' );
            audio.play();
        } catch {
            speakBrowser( text );
        }
    }, [ voiceOn, elKey, elVoice ] );

    const speakBrowser = ( text ) => {
        if ( !voiceOn || !window.speechSynthesis ) return;
        window.speechSynthesis.cancel();
        const utt  = new SpeechSynthesisUtterance( text );
        utt.rate   = 1.0;
        utt.pitch  = 1.1;
        const voices = window.speechSynthesis.getVoices();
        const female = voices.find( v => v.name.toLowerCase().includes('samantha') || v.name.toLowerCase().includes('victoria') || v.name.toLowerCase().includes('karen') );
        if ( female ) utt.voice = female;
        utt.onend = () => setSpeaking( false );
        setSpeaking( true );
        window.speechSynthesis.speak( utt );
    };

    /* ── OpenAI TTS ── */
    const speakOpenAI = useCallback( async ( text ) => {
        if ( !voiceOn || !oaiKey ) return;
        try {
            setSpeaking( true );
            const res = await fetch( 'https://api.openai.com/v1/audio/speech', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + oaiKey,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model: 'tts-1',
                    input: text,
                    voice: oaiVoice,
                }),
            });
            if ( !res.ok ) throw new Error( 'OpenAI TTS error' );
            const blob = URL.createObjectURL( await res.blob() );
            if ( audioRef.current ) {
                audioRef.current.src = blob;
                audioRef.current.onended = () => setSpeaking( false );
                audioRef.current.play();
            }
        } catch( e ) {
            console.error( 'OpenAI TTS error:', e );
            setSpeaking( false );
            speakBrowser( text );
        }
    }, [ voiceOn, oaiKey, oaiVoice ] );

    /* ── Smart speak — only speak short conversational responses ── */
    const speak = useCallback( ( text ) => {
        if ( !voiceOn || !text ) return;

        /* never speak if text contains wallet addresses, tx hashes, seed phrases */
        const hasAddress   = /[A-Za-z0-9]{32,}/.test( text ) || /0x[a-fA-F0-9]{10,}/.test( text );
        const hasTxId      = /TX:|Transaction ID:|txid|Hash:/i.test( text );
        const hasSeedWords = /1\.\s\w+\s+2\.\s\w+/i.test( text );
        const isTooLong    = text.length > 220;

        if ( hasAddress || hasTxId || hasSeedWords || isTooLong ) {
            /* extract a short spoken summary BEFORE stripping emojis */
            let spoken = '';
            if ( text.startsWith( '✅' ) || text.startsWith( 'Transaction complete' ) ) spoken = 'Transaction complete!';
            else if ( text.startsWith( '❌' ) ) spoken = 'Failed. Check screen.';
            else if ( text.startsWith( '⏳' ) ) spoken = 'One moment.';
            else if ( text.startsWith( '🔑' ) ) spoken = 'Seed phrase generated.';
            else if ( text.startsWith( '🎉' ) ) spoken = 'Wallet created!';
            else if ( hasTxId ) spoken = 'Transaction sent.';
            else if ( hasAddress ) spoken = 'Address on screen.';
            else spoken = '';

            if ( spoken ) {
                if ( ttsProvider === 'elevenlabs' && elKey ) speakElevenLabs( spoken );
                else if ( ttsProvider === 'openai' && oaiKey ) speakOpenAI( spoken );
                else speakBrowser( spoken );
            }
            return;
        }

        /* short conversational response — speak in full */
        /* strip emojis from normal text before speaking */
        const cleanText = text.replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
                              .replace(/[\u2600-\u26FF]/g, '')
                              .replace(/[\u2700-\u27BF]/g, '')
                              .replace(/\s{2,}/g, ' ').trim();
        if ( !cleanText ) return;
        if ( ttsProvider === 'elevenlabs' && elKey ) speakElevenLabs( cleanText );
        else if ( ttsProvider === 'openai' && oaiKey ) speakOpenAI( cleanText );
        else speakBrowser( cleanText );
    }, [ voiceOn, ttsProvider, elKey, oaiKey, speakElevenLabs, speakOpenAI ] );

    /* ── Mic / Speech Recognition with interim results ── */
    const startListening = useCallback( () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if ( !SR ) { alert( 'Speech recognition not supported. Try Chrome.' ); return; }
        if ( listening ) { recognRef.current && recognRef.current.stop(); setListening( false ); return; }
        const recog          = new SR();
        recog.lang           = 'en-US';
        recog.continuous     = false;
        recog.interimResults = true;
        recog.onstart  = () => { setListening( true ); playChime( 'start' ); };
        recog.onend    = () => { setListening( false ); setInterimText( '' ); playChime( 'stop' ); };
        recog.onerror  = () => { setListening( false ); setInterimText( '' ); };
        recog.onresult = ( e ) => {
            let interim = '';
            let final   = '';
            for ( let i = e.resultIndex; i < e.results.length; i++ ) {
                if ( e.results[i].isFinal ) final += e.results[i][0].transcript;
                else interim += e.results[i][0].transcript;
            }
            setInterimText( interim );
            if ( final ) {
                setInterimText( '' );
                setTimeout( () => processMessageRef.current( final ), 100 );
            }
        };
        recognRef.current = recog;
        recog.start();
    }, [ listening ] );

    /* ── Clear all seed verification timers ── */
    const clearSeedTimers = useCallback( () => {
        if ( seedTimerRef1.current ) { clearTimeout( seedTimerRef1.current ); seedTimerRef1.current = null; }
        if ( seedTimerRef2.current ) { clearTimeout( seedTimerRef2.current ); seedTimerRef2.current = null; }
        if ( seedTimerRef3.current ) { clearTimeout( seedTimerRef3.current ); seedTimerRef3.current = null; }
    }, [] );

    /* ── Auto listen after Rosie asks for seed words ── */
    const autoListenForSeed = useCallback( () => {
        /* wait 800ms for Rosie to finish speaking then auto-start mic */
        setTimeout( () => {
            if ( recognRef.current ) { try { recognRef.current.stop(); } catch(e) {} }
            const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
            if ( !SR ) return;
            const recog          = new SR();
            recog.lang           = 'en-US';
            recog.continuous     = false;
            recog.interimResults = false;
            recog.onstart  = () => { setListening( true ); playChime( 'start' ); };
            recog.onend    = () => { setListening( false ); setInterimText( '' ); playChime( 'stop' ); };
            recog.onerror  = () => { setListening( false ); setInterimText( '' ); };
            recog.onresult = ( e ) => {
                let final = '';
                for ( let i = e.resultIndex; i < e.results.length; i++ ) {
                    if ( e.results[i].isFinal ) final += e.results[i][0].transcript;
                }
                if ( final ) {
                    clearSeedTimers();
                    setInterimText( '' );
                    setTimeout( () => processMessageRef.current( final ), 100 );
                }
            };
            recognRef.current = recog;
            try { recog.start(); } catch(e) {}

            /* Timer 1 — after 10s say still waiting */
            seedTimerRef1.current = setTimeout( () => {
                const msg1 = "I'm still waiting for your seed words. Please say your first word and last word now.";
                setMessages( prev => [ ...prev, { role: 'assistant', content: msg1 } ] );
                speak( msg1 );

                /* Timer 2 — after another 10s say press mic */
                seedTimerRef2.current = setTimeout( () => {
                    const msg2 = "I didn't receive any response. Press the mic button if you'd like to continue.";
                    setMessages( prev => [ ...prev, { role: 'assistant', content: msg2 } ] );
                    speak( msg2 );

                    /* Timer 3 — after another 10s cancel transaction */
                    seedTimerRef3.current = setTimeout( () => {
                        if ( awaitingSeedRef.current ) {
                            awaitingSeedRef.current = false;
                            setAwaitingSeedVerification( false );
                            setPendingSend( null );
                            pendingSendRef.current = null;
                            const msg3 = '❌ Transaction cancelled. No response received. Your funds are safe.';
                            setMessages( prev => [ ...prev, { role: 'assistant', content: msg3 } ] );
                            speak( msg3 );
                        }
                    }, 10000 );
                }, 10000 );
            }, 10000 );
        }, 800 );
    }, [ clearSeedTimers, speak ] );

    /* ── Network switching ── */
    const NETWORK_KEYS = {
        'ethereum': 'ethereum', 'eth': 'ethereum',
        'solana': 'solana',     'sol': 'solana',
        'bsc': 'bsc',           'binance': 'bsc',       'bnb': 'bsc',
        'polygon': 'polygon',   'matic': 'polygon',
        'base': 'base',
        'arbitrum': 'arbitrum', 'arb': 'arbitrum',
    };

    const handleNetworkSwitch = ( text ) => {
        const lower = text.toLowerCase();
        /* never intercept account/wallet switches */
        if ( lower.includes('account') || lower.includes('wallet') ) return null;
        if ( lower.includes('switch') || lower.includes('change') || lower.includes('go to') || lower.includes('use') ) {
            for ( const [ keyword, netKey ] of Object.entries( NETWORK_KEYS ) ) {
                if ( lower.includes( keyword ) ) {
                    switchNetwork( netKey );
                    const netName = netKey.charAt(0).toUpperCase() + netKey.slice(1);
                    return 'Switched to ' + netName + ' network!';
                }
            }
        }
        return null;
    };

    /* ── Navigation ── */
    const navRoutes = {
        'wallet': '/', 'transactions': '/transactions', 'exchange': '/exchange',
        'launchpad': '/launchpad', 'predict': '/predict', 'p2p': '/p2p',
        'apps': '/apps', 'market': '/market', 'settings': '/settings',
    };

    const handleNavigation = ( text ) => {
        const lower = text.toLowerCase();
        for ( const [ key, route ] of Object.entries( navRoutes ) ) {
            if ( lower.includes('open ' + key) || lower.includes('go to ' + key) || lower.includes('show ' + key) || lower.includes('take me to ' + key) ) {
                window.location.hash = route;
                return `Opening ${ key.charAt(0).toUpperCase() + key.slice(1) } for you! 🚀`;
            }
        }
        return null;
    };

    /* ── Create new wallet account ── */
    const handleCreateAccount = async ( text ) => {
        const lower = text.toLowerCase();
        /* must be an EXPLICIT create request — not just mentioning wallet or account */
        const explicitCreate = 
            lower.includes('create new') ||
            lower.includes('create a new') ||
            lower.includes('create another') ||
            lower.includes('add new wallet') ||
            lower.includes('add another wallet') ||
            lower.includes('new wallet') ||
            lower.includes('new account') ||
            lower.includes('add account') ||
            lower.includes('create account') ||
            lower.includes('create wallet');
        if ( !explicitCreate ) return null;

        if ( !builtInWallet || !builtInWallet.mnemonic ) {
            return 'You need to set up your built-in wallet first in Settings.';
        }

        try {
            const stored   = localStorage.getItem( 'chainvault_accounts' );
            const accounts = stored ? JSON.parse( stored ) : [];
            const newIndex = accounts.length;
            const derived  = await deriveAccount( builtInWallet.mnemonic, newIndex );
            const newAcc   = {
                index:           newIndex,
                label:           'Account ' + ( newIndex + 1 ),
                ethereumAddress: derived.ethereumAddress,
                privateKey:      derived.privateKey,
                solanaAddress:   derived.solanaAddress,
                solanaSecretKey: derived.solanaSecretKey || null,
            };
            const updated = [ ...accounts, newAcc ];
            localStorage.setItem( 'chainvault_accounts', JSON.stringify( updated ) );

            /* auto switch to new account */
            await switchAccount( newAcc );
            localStorage.setItem( 'chainvault_active_account', newIndex.toString() );

            return '✅ Account ' + ( newIndex + 1 ) + ' created and activated! Your new ETH address is ' + derived.ethereumAddress.slice(0,10) + '... and SOL address is ' + derived.solanaAddress.slice(0,6) + '... You are now on Account ' + ( newIndex + 1 ) + '.';
        } catch(e) {
            return 'Failed to create account: ' + e.message;
        }
    };

    /* ── Send crypto flow with seed verification ── */
    const handleSendIntent = ( text ) => {
        const lower = text.toLowerCase();
        if ( !lower.includes( 'send' ) ) return null;

        /* parse amount and token */
        const amountMatch = text.match( /(\d+\.?\d*)\s*(solana|sol(?!d)|ethereum|eth|bnb|binance|matic|polygon|base|arbitrum|arb)/i );
        if ( !amountMatch ) return null;

        const amount   = amountMatch[1];
        const tokenRaw = amountMatch[2].toLowerCase();
        const tokenMap = {
            'sol':      'SOL',
            'solana':   'SOL',
            'eth':      'ETH',
            'ethereum': 'ETH',
            'bnb':      'BNB',
            'binance':  'BNB',
            'matic':    'MATIC',
            'polygon':  'MATIC',
            'base':     'ETH',
            'arb':      'ETH',
            'arbitrum': 'ETH',
        };
        const token    = tokenMap[ tokenRaw ] || tokenRaw.toUpperCase();

        /* check if sending to account/wallet number */
        const accountMatch = text.match( /to\s+(?:account|wallet)\s*(\d+)/i );

        let toAddr = null;
        let toLabel = '';

        if ( accountMatch ) {
            const targetIdx = parseInt( accountMatch[1] ) - 1;
            const stored    = localStorage.getItem( 'chainvault_accounts' );
            const accounts  = stored ? JSON.parse( stored ) : [];

            if ( !accounts[ targetIdx ] ) {
                return 'Account ' + accountMatch[1] + ' does not exist. You currently have ' + accounts.length + ' account(s).';
            }

            if ( token === 'SOL' ) {  /* solana/sol both map here */
                toAddr  = accounts[ targetIdx ].solanaAddress;
                toLabel = 'Account ' + accountMatch[1];
            } else {
                toAddr  = accounts[ targetIdx ].ethereumAddress;
                toLabel = 'Account ' + accountMatch[1];
            }
        } else {
            /* direct address */
            const toMatch = text.match( /to\s+([A-Za-z0-9]{10,})/i );
            if ( !toMatch ) {
                return 'Please specify where to send. Example: "send 0.01 SOL to account 2" or "send 0.01 SOL to 3A69..."';
            }
            toAddr  = toMatch[1];
            toLabel = toAddr;
        }

        if ( !toAddr ) return 'Could not find destination address.';

        setPendingSend( { amount, token, toAddr } );
        pendingSendRef.current = { amount, token, toAddr };
        setAwaitingSeedVerification( true );
        awaitingSeedRef.current = true;

        return 'I will send ' + amount + ' ' + token + ' to ' + toLabel + '. To confirm, please tell me the FIRST word and LAST word of your seed phrase only.';
    };

    /* ── Verify seed phrase and execute send ── */
    const handleSeedVerification = async ( text ) => {
        if ( (!pendingSend && !pendingSendRef.current) || !builtInWallet || !builtInWallet.mnemonic ) {
            setAwaitingSeedVerification( false );
            awaitingSeedRef.current = false;
            setPendingSend( null );
            pendingSendRef.current = null;
            return 'Transaction cancelled.';
        }

        const words      = builtInWallet.mnemonic.trim().split( /\s+/ );
        const firstWord  = words[0].toLowerCase();
        const lastWord   = words[ words.length - 1 ].toLowerCase();
        const inputLower = text.toLowerCase();

        const hasFirst = inputLower.includes( firstWord );
        const hasLast  = inputLower.includes( lastWord );

        if ( !hasFirst || !hasLast ) {
            clearSeedTimers();
            setAwaitingSeedVerification( false );
            awaitingSeedRef.current = false;
            setPendingSend( null );
            pendingSendRef.current = null;
            return '❌ Verification failed. Transaction cancelled for your security.';
        }

        /* verified — execute transaction */
        clearSeedTimers();
        setAwaitingSeedVerification( false );
        awaitingSeedRef.current = false;
        const { amount, token, toAddr } = pendingSendRef.current || pendingSend;
        setPendingSend( null );
        pendingSendRef.current = null;

        /* show "please wait" immediately */
        const waitMsg = 'Got it! Sending ' + amount + ' ' + token + '. Please wait while I process this on the blockchain...';
        setMessages( prev => [ ...prev, { role: 'assistant', content: waitMsg } ] );
        speak( 'Got it! Please wait while I process your transaction on the blockchain.' );

        /* run transaction async so UI stays responsive */
        ( async () => {
            try {
                const stored     = localStorage.getItem( 'chainvault_accounts' );
                const accounts   = stored ? JSON.parse( stored ) : [];
                const activeIdx  = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
                const activeAcct = accounts[ activeIdx ];

                if ( !activeAcct ) throw new Error( 'No active account found' );

                let txid = null;

                if ( token === 'SOL' ) {
                    const { sendSolTransaction } = await import( '../utils/wallet' );
                    const secretKey = activeAcct.solanaSecretKey || builtInWallet.solana.secretKey;
                    if ( !secretKey ) throw new Error( 'No Solana secret key found. Please recreate the account in Settings.' );
                    txid = await sendSolTransaction(
                        secretKey,
                        toAddr,
                        parseFloat( amount ),
                        'https://api.mainnet-beta.solana.com'
                    );
                } else {
                    const { sendEthTransaction } = await import( '../utils/wallet' );
                    const rpcMap = {
                        ethereum: 'https://eth.llamarpc.com',
                        bsc:      'https://bsc-dataseed.binance.org',
                        polygon:  'https://polygon-rpc.com',
                        base:     'https://mainnet.base.org',
                        arbitrum: 'https://arb1.arbitrum.io/rpc',
                    };
                    const rpc = rpcMap[ selectedNetwork ] || rpcMap.ethereum;
                    txid = await sendEthTransaction( activeAcct.privateKey, toAddr, amount, rpc );
                }

                const completeMsg = 'Transaction complete! Here is your transaction hash:\n\n' + txid + '\n\nYou can verify it on the Transactions page.';
                setMessages( prev => [ ...prev, { role: 'assistant', content: completeMsg } ] );
                speak( 'Transaction complete! Your hash is on screen.' );

            } catch ( err ) {
                console.error('Rosie send error:', err);
                const failMsg = 'Transaction failed: ' + ( err.message || 'Unknown error' ) + '. Please check your balance and try again.';
                setMessages( prev => [ ...prev, { role: 'assistant', content: failMsg } ] );
                speak( 'Transaction failed. ' + ( err.message || 'Please check your balance.' ) );
            }
            setLoading( false );
        })();

        return null; /* loading handled inside async block */
    };

    /* ── Core message processor ── */
    const processMessage = useCallback( async ( text ) => {
        if ( !text || loading ) return;

        /* ── Wake word / greeting handler ── */
        const lowerGreet = text.toLowerCase().trim();
        const isGreeting = lowerGreet === 'hi' || lowerGreet === 'hello' ||
                           lowerGreet === 'hi rosie' || lowerGreet === 'hello rosie' ||
                           lowerGreet === 'hey rosie' || lowerGreet === 'hey' ||
                           lowerGreet.match(/^(hi|hey|hello)\s*[!.]*$/) !== null;
        if ( isGreeting ) {
            const name = userNameRef.current;
            const greet = name ? `👋 Hey ${ name }! How may I help you today?` : '👋 Hey! How may I help you today?';
            setMessages( prev => [ ...prev, { role: 'user', content: text }, { role: 'assistant', content: greet } ] );
            speak( greet );
            setLoading( false );
            return;
        }

        /* ── Name detection ── */
        const nameMatch = text.match( /my name is ([A-Za-z]+)/i ) || text.match( /call me ([A-Za-z]+)/i ) || text.match( /i am ([A-Za-z]+)/i );
        if ( nameMatch ) {
            const detectedName = nameMatch[1];
            setUserName( detectedName );
            userNameRef.current = detectedName;
            try { sessionStorage.setItem('rosie_username', detectedName); } catch(e) {}
            const reply = `Got it! I'll call you ${ detectedName } from now on. 😊 How can I help you today?`;
            setMessages( prev => [ ...prev, { role: 'user', content: text }, { role: 'assistant', content: reply } ] );
            speak( reply );
            setLoading( false );
            return;
        }

        const userMsg     = { role: 'user', content: text };
        const newMessages = [ ...messages, userMsg ];
        setMessages( newMessages );
        setLoading( true );

        /* onboarding copy confirmation flow */
        if ( awaitingCopyRef.current ) {
            const lower = text.toLowerCase();
            const confirmed = lower.includes('yes') || lower.includes('yeah') || lower.includes('yep') ||
                              lower.includes('done') || lower.includes('copied') || lower.includes('sure') ||
                              lower.includes('ok') || lower.includes('okay') || lower.includes('i have');
            if ( confirmed ) {
                awaitingCopyRef.current = false;
                setAwaitingCopyConfirm( false );
                const mnemonic = onboardingMnemonic;
                setOnboardingMnemonic( null );
                try {
                    const { createWalletFromSeed } = await import( '../utils/wallet' );
                    const walletData = await createWalletFromSeed( mnemonic );
                    await handleBuiltInWalletCreated( walletData );
                    const reply = '🎉 Your wallet has been created successfully! Welcome to ChainVault. You can now send, receive and manage your crypto.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                } catch(e) {
                    const reply = '❌ Failed to create wallet: ' + e.message + '. Please try again.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                }
                setLoading( false );
                return;
            } else {
                const reply = "I need you to confirm you've saved your seed phrase before I can continue. Just say yes when you're ready.";
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply );
                setLoading( false );
                return;
            }
        }

        /* onboarding mode — only allow wallet creation */
        if ( showOnboarding ) {
            const lower = text.toLowerCase();
            const wantsWallet = lower.includes('create') || lower.includes('new wallet') ||
                                lower.includes('get started') || lower.includes('start') ||
                                lower.includes('yes') || lower.includes('wallet') || lower.includes('account');
            if ( wantsWallet && !onboardingMnemonic ) {
                try {
                    const { generateMnemonic } = await import( 'bip39' );
                    const mnemonic = generateMnemonic();
                    setOnboardingMnemonic( mnemonic );
                    const words = mnemonic.trim().split( ' ' );
                    const wordList = words.map( (w,i) => (i+1) + '. ' + w ).join('  ');
                    const reply = '🔑 I have generated your 12-word seed phrase. Please write these down carefully:\n\n' + wordList + '\n\nThese words are the ONLY way to recover your wallet. Never share them with anyone. Say YES when you have copied them safely.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( 'I have generated your 12 word seed phrase. Please write these down carefully. ' + words.join(', ') + '. Say yes when you have copied them safely.' );
                    setAwaitingCopyConfirm( true );
                    awaitingCopyRef.current = true;
                    /* auto listen for yes */
                    setTimeout( () => autoListenForSeed(), 4000 );
                } catch(e) {
                    const reply = '❌ Failed to generate seed phrase. Please try using the regular setup button.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                }
                setLoading( false );
                return;
            } else if ( !wantsWallet ) {
                const reply = "I can only help you create a new wallet right now. Just say 'create wallet' or 'get started' and I'll guide you through it!";
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply );
                setLoading( false );
                return;
            }
        }

        /* seed verification flow — use ref to avoid stale closure */
        if ( awaitingSeedRef.current ) {
            const result = await handleSeedVerification( text );
            /* result is null when send is handled async — just return, async block handles messages */
            if ( result !== null ) {
                setMessages( prev => [ ...prev, { role: 'assistant', content: result } ] );
                speak( result );
            }
            setLoading( false );
            return;
        }

        /* ── CONTACT TICKET FLOW — runs first before all other handlers ── */
        const currentFlow = contactFlowRef.current || contactFlow;
        if ( currentFlow ) {
            const step = currentFlow.step;
            if ( step === 'name' ) {
                const newFlow = { ...currentFlow, name: text.trim(), step: 'email' };
                setContactFlow( newFlow ); contactFlowRef.current = newFlow;
                const reply = 'Got it, ' + text.trim() + '! What email address should support reply to?';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply ); setLoading( false ); return;
            }
            if ( step === 'email' ) {
                const newFlow = { ...currentFlow, email: text.trim(), step: 'message' };
                setContactFlow( newFlow ); contactFlowRef.current = newFlow;
                const reply = 'Perfect! Now describe your issue and I will send it straight to support for you.';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply ); setLoading( false ); return;
            }
            if ( step === 'message' ) {
                const { name, email } = currentFlow;
                const message  = text.trim();
                const apiBase  = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
                const appName  = window.chainvaultSettings?.branding?.app_name || 'ChainVault';
                setContactFlow( null ); contactFlowRef.current = null;
                try {
                    const res  = await fetch( apiBase + '/contact', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name, email, subject: 'Support Request via ' + appName + ' AI', message }),
                    });
                    const data = await res.json();
                    const reply = data.success
                        ? '✅ Done! Your message has been sent to support. They will get back to you at ' + email + ' as soon as possible.'
                        : '❌ Something went wrong. Please try the Contact Us link in the footer.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                } catch(e) {
                    const reply = '❌ Could not reach support right now. Please try the Contact Us link in the footer.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                }
                setLoading( false ); return;
            }
        }

        /* Contact/support intent detection — runs before balance/price/Claude */
        const lowerContact = text.toLowerCase();
        const wantsContact =
            lowerContact.includes('i need support') || lowerContact.includes('i need help') ||
            lowerContact.includes('i want help') || lowerContact.includes('i need assistance') ||
            lowerContact.includes('help me please') || lowerContact.includes('submit a ticket') ||
            lowerContact.includes('submit ticket') || lowerContact.includes('create a ticket') ||
            lowerContact.includes('create ticket') || lowerContact.includes('open a ticket') ||
            lowerContact.includes('open ticket') || lowerContact.includes('raise a ticket') ||
            lowerContact.includes('raise ticket') || lowerContact.includes('support ticket') ||
            lowerContact.includes('i want to submit') || lowerContact.includes('i want to open a ticket') ||
            lowerContact.includes('contact support') || lowerContact.includes('contact admin') ||
            lowerContact.includes('contact us') || lowerContact.includes('reach support') ||
            lowerContact.includes('talk to support') || lowerContact.includes('message support') ||
            lowerContact.includes('get support') || lowerContact.includes('message admin') ||
            lowerContact.includes('speak to someone') || lowerContact.includes('speak to a human') ||
            lowerContact.includes('i have a complaint') || lowerContact.includes('report an issue') ||
            lowerContact.includes('report a problem') || lowerContact.includes('i found a bug') ||
            lowerContact.includes("there's a bug") || lowerContact.includes('there is a bug') ||
            lowerContact.includes('i want to complain') || lowerContact.includes('i want to complaint') ||
            lowerContact.includes('send a message to support') || lowerContact.includes('message the admin');
        if ( wantsContact ) {
            const newFlow = { step: 'name' };
            setContactFlow( newFlow ); contactFlowRef.current = newFlow;
            const reply = "I'll help you reach support right away! First, what's your name?";
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( reply ); setLoading( false ); return;
        }

        /* network switch */
        const netResponse = handleNetworkSwitch( text );
        if ( netResponse ) {
            setMessages( prev => [ ...prev, { role: 'assistant', content: netResponse } ] );
            speak( netResponse );
            setLoading( false );
            return;
        }

        /* navigation */
        const navResponse = handleNavigation( text );
        if ( navResponse ) {
            setMessages( prev => [ ...prev, { role: 'assistant', content: navResponse } ] );
            speak( navResponse );
            setLoading( false );
            return;
        }

        /* create account */
        const accResponse = await handleCreateAccount( text );
        if ( accResponse ) {
            setMessages( prev => [ ...prev, { role: 'assistant', content: accResponse } ] );
            speak( accResponse );
            setLoading( false );
            return;
        }

            /* switch account */
        if ( text.toLowerCase().includes('switch') && ( text.toLowerCase().includes('account') || text.toLowerCase().includes('wallet') ) ) {
            /* support both digits and spoken words: one=1, two=2 ... ten=10 */
            const wordToNum = { 'one':1,'two':2,'three':3,'four':4,'five':5,'six':6,'seven':7,'eight':8,'nine':9,'ten':10 };
            let accountNum = null;
            const numMatch = text.match( /(\d+)/ );
            if ( numMatch ) {
                accountNum = parseInt( numMatch[1] );
            } else {
                const words = text.toLowerCase().split( /\s+/ );
                for ( const w of words ) {
                    if ( wordToNum[w] ) { accountNum = wordToNum[w]; break; }
                }
            }
            if ( accountNum !== null ) {
                const targetIdx = accountNum - 1;
                const stored    = localStorage.getItem( 'chainvault_accounts' );
                const accounts  = stored ? JSON.parse( stored ) : [];
                if ( accounts[ targetIdx ] ) {
                    localStorage.setItem( 'chainvault_active_account', targetIdx.toString() );
                    switchAccount( accounts[ targetIdx ] ); /* fire and forget — don't await */
                    const acc   = accounts[ targetIdx ];
                    const reply = 'Switched to Account ' + accountNum + '! You\'re all set.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                    setLoading( false );
                    return;
                } else {
                    const reply = 'Account ' + accountNum + ' does not exist. You have ' + accounts.length + ' account(s). Say "create new wallet" to add one.';
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( reply );
                    setLoading( false );
                    return;
                }
            }
        }

        /* ── PRICE HANDLER ── */
        const lowerP = text.toLowerCase();
        const wantsPrice = lowerP.includes('price') || lowerP.includes('worth') ||
                           lowerP.includes('how much is') || lowerP.includes('how much does') ||
                           lowerP.includes('cost of') || lowerP.includes('value of') ||
                           lowerP.includes('trading at') || lowerP.includes('market price');

        if ( wantsPrice && !lowerP.includes('send') && !lowerP.includes('address') ) {
            const coinMap = {
                'bitcoin':'bitcoin','btc':'bitcoin',
                'ethereum':'ethereum','eth':'ethereum',
                'solana':'solana','sol':'solana',
                'bnb':'binancecoin','binance':'binancecoin',
                'polygon':'matic-network','matic':'matic-network',
                'cardano':'cardano','ada':'cardano',
                'xrp':'ripple','ripple':'ripple',
                'dogecoin':'dogecoin','doge':'dogecoin',
                'shib':'shiba-inu','shiba':'shiba-inu',
                'avalanche':'avalanche-2','avax':'avalanche-2',
                'chainlink':'chainlink','link':'chainlink',
                'litecoin':'litecoin','ltc':'litecoin',
                'polkadot':'polkadot','dot':'polkadot',
                'tron':'tron','trx':'tron',
                'near':'near','uniswap':'uniswap','uni':'uniswap',
                'arbitrum':'arbitrum','arb':'arbitrum',
                'optimism':'optimism','op':'optimism',
            };
            let coinId = null; let coinName = null;
            for ( const [key, val] of Object.entries( coinMap ) ) {
                if ( lowerP.includes( key ) ) { coinId = val; coinName = key.toUpperCase(); break; }
            }
            try {
                if ( coinId ) {
                    const res  = await fetch( 'https://api.coingecko.com/api/v3/simple/price?ids=' + coinId + '&vs_currencies=usd&include_24hr_change=true&include_market_cap=true' );
                    const data = await res.json();
                    const coin = data[ coinId ];
                    if ( !coin ) throw new Error( 'No data' );
                    const change = coin.usd_24h_change?.toFixed(2);
                    const arrow  = coin.usd_24h_change > 0 ? '▲' : '▼';
                    const mcap   = coin.usd_market_cap >= 1e9 ? '$' + (coin.usd_market_cap/1e9).toFixed(2)+'B' : '$' + (coin.usd_market_cap/1e6).toFixed(2)+'M';
                    const reply  = coinName + ' — $' + coin.usd?.toLocaleString('en-US',{maximumFractionDigits:6}) + ' ' + arrow + ' ' + change + '% (24h) | Market cap: ' + mcap;
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( coinName + ' is $' + coin.usd?.toLocaleString('en-US',{maximumFractionDigits:2}) + '.' );
                } else {
                    const res  = await fetch( 'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,binancecoin&vs_currencies=usd&include_24hr_change=true' );
                    const data = await res.json();
                    const fmt  = (id,sym) => sym + ': $' + data[id]?.usd?.toLocaleString() + ' (' + (data[id]?.usd_24h_change>0?'+':'') + data[id]?.usd_24h_change?.toFixed(2) + '%)';
                    const reply = fmt('bitcoin','BTC') + ' | ' + fmt('ethereum','ETH') + ' | ' + fmt('solana','SOL') + ' | ' + fmt('binancecoin','BNB');
                    setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                    speak( 'Prices on screen.' );
                }
            } catch(e) {
                const reply = 'Could not fetch price right now. Please check the Market page.';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply );
            }
            setLoading( false );
            return;
        }

        /* address request handler */
        const lowerAddr = text.toLowerCase();
        const wantsAddress = ( lowerAddr.includes('address') || lowerAddr.includes('wallet address') ||
                             lowerAddr.includes('my address') || lowerAddr.includes('deposit address') ||
                             lowerAddr.includes('receive address') ) &&
                             !lowerAddr.includes('switch') && !lowerAddr.includes('account');

        if ( wantsAddress ) {
            const stored   = localStorage.getItem( 'chainvault_accounts' );
            const accounts = stored ? JSON.parse( stored ) : [];

            /* check if asking for a specific account */
            const wordToNum = { 'one':1,'two':2,'three':3,'four':4,'five':5,'six':6,'seven':7,'eight':8,'nine':9,'ten':10 };
            let accountNum  = null;
            const numMatch  = text.match( /account\s*(\d+)/i ) || text.match( /wallet\s*(\d+)/i );
            if ( numMatch ) {
                accountNum = parseInt( numMatch[1] );
            } else {
                const words = lowerAddr.split( /\s+/ );
                for ( const w of words ) {
                    if ( wordToNum[w] ) { accountNum = wordToNum[w]; break; }
                }
            }

            const targetIdx = accountNum !== null
                ? accountNum - 1
                : parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );

            const targetAcct = accounts[ targetIdx ];

            if ( !targetAcct && accountNum !== null ) {
                const reply = 'Account ' + accountNum + ' does not exist. You have ' + accounts.length + ' account(s).';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply );
                setLoading( false );
                return;
            }

            const acct = targetAcct || { ethereumAddress: builtInWallet?.ethereum?.address, solanaAddress: builtInWallet?.solana?.address };
            const accLabel = 'Account ' + ( targetIdx + 1 );

            /* detect which network/chain is being asked */
            const wantsSol  = lowerAddr.includes('sol') || lowerAddr.includes('solana');
            const wantsBtc  = lowerAddr.includes('btc') || lowerAddr.includes('bitcoin');
            const wantsEth  = lowerAddr.includes('eth') || lowerAddr.includes('ethereum');
            const wantsBnb  = lowerAddr.includes('bnb') || lowerAddr.includes('binance') || lowerAddr.includes('bsc');
            const wantsMatic = lowerAddr.includes('matic') || lowerAddr.includes('polygon');

            let reply = '';

            if ( wantsSol ) {
                const addr = acct.solanaAddress || builtInWallet?.solana?.address || 'Not found';
                reply = accLabel + ' Solana address: ' + addr;
            } else if ( wantsBtc ) {
                const addr = builtInWallet?.bitcoin?.address || 'Not found';
                reply = accLabel + ' Bitcoin address: ' + addr;
            } else if ( wantsBnb ) {
                const addr = acct.ethereumAddress || 'Not found';
                reply = accLabel + ' BNB address (same as ETH): ' + addr;
            } else if ( wantsMatic ) {
                const addr = acct.ethereumAddress || 'Not found';
                reply = accLabel + ' Polygon address (same as ETH): ' + addr;
            } else if ( wantsEth ) {
                const addr = acct.ethereumAddress || 'Not found';
                reply = accLabel + ' Ethereum address: ' + addr;
            } else {
                /* show all addresses */
                reply = accLabel + ' addresses — ETH/BNB/Polygon: ' + ( acct.ethereumAddress || 'Not found' ) + ' | SOL: ' + ( acct.solanaAddress || builtInWallet?.solana?.address || 'Not found' ) + ' | BTC: ' + ( builtInWallet?.bitcoin?.address || 'Not found' );
            }

            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( 'Address on screen.' );
            setLoading( false );
            return;
        }

        /* ── BALANCE QUESTIONS ── */
        const lowerBal = text.toLowerCase();
        const wantsBalance = lowerBal.includes('balance') || lowerBal.includes('how much') ||
                             lowerBal.includes('how many') || lowerBal.includes('do i have') ||
                             lowerBal.includes('my sol') || lowerBal.includes('my eth') ||
                             lowerBal.includes('my bnb') || lowerBal.includes('my matic') ||
                             lowerBal.includes('my bitcoin') || lowerBal.includes('my btc');

        if ( wantsBalance && !lowerBal.includes('send') && !lowerBal.includes('price') ) {
            const activeIdx  = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
            const stored     = localStorage.getItem( 'chainvault_accounts' );
            const accounts   = stored ? JSON.parse( stored ) : [];
            const activeAcct = accounts[ activeIdx ];
            const accLabel   = 'Account ' + ( activeIdx + 1 );

            const solBal  = balances?.solana   || '0';
            const ethBal  = balances?.ethereum || balances?.eth || '0';
            const bnbBal  = balances?.bsc      || '0';
            const maticBal = balances?.polygon  || '0';
            const baseBal = balances?.base     || '0';

            let reply = '';
            if ( lowerBal.includes('sol') || lowerBal.includes('solana') ) {
                reply = accLabel + ' Solana balance: ' + parseFloat(solBal).toFixed(6) + ' SOL';
            } else if ( lowerBal.includes('eth') || lowerBal.includes('ethereum') ) {
                reply = accLabel + ' Ethereum balance: ' + parseFloat(ethBal).toFixed(6) + ' ETH';
            } else if ( lowerBal.includes('bnb') || lowerBal.includes('binance') ) {
                reply = accLabel + ' BNB balance: ' + parseFloat(bnbBal).toFixed(6) + ' BNB';
            } else if ( lowerBal.includes('matic') || lowerBal.includes('polygon') ) {
                reply = accLabel + ' Polygon balance: ' + parseFloat(maticBal).toFixed(6) + ' MATIC';
            } else if ( lowerBal.includes('btc') || lowerBal.includes('bitcoin') ) {
                reply = accLabel + ' Bitcoin balance: ' + ( balances?.bitcoin || '0' ) + ' BTC';
            } else {
                /* show all balances */
                reply = accLabel + ' balances — SOL: ' + parseFloat(solBal).toFixed(6) + ' | ETH: ' + parseFloat(ethBal).toFixed(6) + ' | BNB: ' + parseFloat(bnbBal).toFixed(6) + ' | MATIC: ' + parseFloat(maticBal).toFixed(6) + ' | Base: ' + parseFloat(baseBal).toFixed(6);
            }
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( 'Balance on screen.' );
            setLoading( false );
            return;
        }

        /* ── CURRENT NETWORK / ACCOUNT ── */
        const wantsNetworkInfo = lowerBal.includes('what network') || lowerBal.includes('which network') ||
                                 lowerBal.includes('what account') || lowerBal.includes('which account') ||
                                 lowerBal.includes('current network') || lowerBal.includes('current account') ||
                                 lowerBal.includes('what wallet') || lowerBal.includes('which wallet') ||
                                 ( lowerBal.includes('am i on') && ( lowerBal.includes('network') || lowerBal.includes('account') || lowerBal.includes('wallet') ) );

        if ( wantsNetworkInfo ) {
            const activeIdx  = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
            const stored     = localStorage.getItem( 'chainvault_accounts' );
            const accounts   = stored ? JSON.parse( stored ) : [];
            const reply = 'You are on ' + ( currentNetwork?.name || selectedNetwork ) + ' network, Account ' + ( activeIdx + 1 ) + ' of ' + accounts.length + '.';
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( reply );
            setLoading( false );
            return;
        }

        /* ── WALLET COUNT ── */
        const wantsWalletCount = lowerBal.includes('how many wallet') || lowerBal.includes('how many account') ||
                                 lowerBal.includes('number of wallet') || lowerBal.includes('number of account') ||
                                 lowerBal.includes('total wallet') || lowerBal.includes('total account');

        if ( wantsWalletCount ) {
            const stored   = localStorage.getItem( 'chainvault_accounts' );
            const accounts = stored ? JSON.parse( stored ) : [];
            const reply    = 'You have ' + accounts.length + ' wallet account' + ( accounts.length === 1 ? '' : 's' ) + ' in ChainVault.';
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( reply );
            setLoading( false );
            return;
        }

        /* ── TRANSACTION HISTORY ── */
        const wantsTx = lowerBal.includes('transaction') || lowerBal.includes('history') ||
                        lowerBal.includes('sent') || lowerBal.includes('received') ||
                        lowerBal.includes('transfer') || lowerBal.includes('did my') ||
                        lowerBal.includes('show my tx') || lowerBal.includes('recent tx');

        if ( wantsTx ) {
            const reply = 'Opening your transaction history now.';
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( reply );
            setTimeout( () => { window.location.hash = '/transactions'; }, 500 );
            setLoading( false );
            return;
        }

        /* ── GAS / FEE QUESTIONS ── */
        const wantsGas = lowerBal.includes('gas') || lowerBal.includes('fee') ||
                         lowerBal.includes('how much to send') || lowerBal.includes('cost to send') ||
                         lowerBal.includes('transaction fee') || lowerBal.includes('network fee');

        if ( wantsGas ) {
            try {
                const res  = await fetch( 'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd' );
                const data = await res.json();
                const ethPrice = data?.ethereum?.usd || 3000;
                const gweiCost = 21000 * 20 / 1e9; /* 20 gwei * 21000 gas */
                const usdCost  = ( gweiCost * ethPrice ).toFixed(4);
                const reply = 'Approximate fees — ETH: ~$' + usdCost + ' | SOL: ~$0.00025 | BNB: ~$0.10 | Polygon: ~$0.01 | Base: ~$0.05';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( 'Fees on screen.' );
            } catch(e) {
                const reply = 'Approximate fees: Ethereum ~$1-5, Solana ~$0.00025, BNB ~$0.10, Polygon ~$0.01';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( 'Fees on screen.' );
            }
            setLoading( false );
            return;
        }

        /* ── SECURITY QUESTIONS ── */
        const wantsSecurity = lowerBal.includes('safe') || lowerBal.includes('secure') ||
                              lowerBal.includes('private key') || lowerBal.includes('seed phrase stored') ||
                              lowerBal.includes('where is my seed') || lowerBal.includes('who can see') ||
                              lowerBal.includes('hack') || lowerBal.includes('backup');

        if ( wantsSecurity ) {
            const reply = 'Your private keys and seed phrase are stored only in your browser — never on any server. ChainVault is fully non-custodial. Nobody including us can access your funds. Always back up your seed phrase in a safe offline place.';
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( 'Your keys are safe, stored only on your device.' );
            setLoading( false );
            return;
        }

        /* ── CHAINVAULT FEATURES ── */
        const wantsFeature = lowerBal.includes('how do i swap') || lowerBal.includes('how to swap') ||
                             lowerBal.includes('what is launchpad') || lowerBal.includes('what is p2p') ||
                             lowerBal.includes('what is predict') || lowerBal.includes('what is yield') ||
                             lowerBal.includes('how do i use') || lowerBal.includes('what does') ||
                             lowerBal.includes('what is the exchange') || lowerBal.includes('how does');

        if ( wantsFeature ) {
            const featureMap = {
                'swap':       'Use the Exchange page to swap tokens on ETH, BSC, Polygon and Base networks.',
                'exchange':   'The Exchange page lets you swap any token on ETH, BSC, Polygon and Base.',
                'launchpad':  'The Launchpad lets you create ERC20 and SPL tokens and join IDO presales.',
                'p2p':        'P2P Trading lets you trade directly with other users — no registration needed, your wallet is your identity.',
                'predict':    'The Predict page lets you bet on crypto prices and world events.',
                'yield':      'Yield Farming in the Apps section shows you farming opportunities across chains.',
                'nft':        'The NFT Gallery in Apps shows all your NFTs across all chains.',
                'market':     'The Market page shows live prices, charts and news for all major cryptos.',
                'gas tracker':'The Gas Tracker in Apps shows real-time gas prices across all networks.',
                'portfolio':  'The Portfolio Tracker in Apps shows your total holdings across all chains.',
            };
            let reply = '';
            for ( const [key, val] of Object.entries( featureMap ) ) {
                if ( lowerBal.includes( key ) ) { reply = val; break; }
            }
            if ( !reply ) reply = 'ChainVault has: Wallet, Exchange, Launchpad, Predict, P2P Trading, Apps (Portfolio, NFT, Yield, Gas Tracker), Market and Settings. Which feature would you like to know more about?';
            setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
            speak( reply.split('.')[0] + '.' );
            setLoading( false );
            return;
        }

        /* ── GENERAL CRYPTO KNOWLEDGE ── */
        const cryptoKnowledge = {
            'what is blockchain':     'A blockchain is a decentralized digital ledger that records transactions across many computers. No single person controls it.',
            'what is defi':           'DeFi (Decentralized Finance) is financial services built on blockchain — no banks needed. You can lend, borrow, trade and earn interest.',
            'what is gas':            'Gas is the fee paid to process transactions on Ethereum. It goes to validators who secure the network.',
            'what is staking':        'Staking means locking up crypto to help secure a blockchain network. In return you earn rewards — like earning interest.',
            'what is nft':            'An NFT (Non-Fungible Token) is a unique digital asset on the blockchain. It proves ownership of digital art, music, collectibles etc.',
            'what is a smart contract': 'A smart contract is self-executing code on the blockchain. It runs automatically when conditions are met — no middleman needed.',
            'what is liquidity':      'Liquidity means how easily an asset can be bought or sold. High liquidity = easy to trade. Low liquidity = hard to sell.',
            'what is market cap':     'Market cap = price × total supply. It shows the total value of a cryptocurrency. Bitcoin has the highest market cap.',
            'what is a dex':          'A DEX (Decentralized Exchange) lets you trade crypto directly from your wallet — no account needed. Examples: Uniswap, Jupiter.',
            'what is slippage':       'Slippage is the difference between expected and actual trade price. High slippage happens with low liquidity or large trades.',
            'what is a token':        'A token is a crypto asset built on an existing blockchain like ETH. A coin is the native currency of its own blockchain like BTC or SOL.',
            'what is yield farming':  'Yield farming means providing liquidity to DeFi protocols to earn rewards. Higher rewards usually mean higher risk.',
            'what is an ido':         'IDO (Initial DEX Offering) is how new crypto projects raise funds. You buy tokens early at a low price before they list on exchanges.',
            'what is web3':           'Web3 is the next generation of the internet built on blockchain — giving users ownership of their data and digital assets.',
            'what is a seed phrase':  'A seed phrase is 12 or 24 words that back up your entire wallet. Anyone with these words can access all your funds — never share them.',
            'what is a private key':  'A private key is a secret code that proves you own your crypto. Your seed phrase generates your private keys automatically.',
        };

        for ( const [question, answer] of Object.entries( cryptoKnowledge ) ) {
            if ( lowerBal.includes( question.replace('what is ', '').replace('what is a ', '') ) ||
                 lowerBal.includes( question ) ) {
                setMessages( prev => [ ...prev, { role: 'assistant', content: answer } ] );
                speak( answer.split('.')[0] + '.' );
                setLoading( false );
                return;
            }
        }

        /* ── PORTFOLIO VALUE ── */
        const wantsPortfolio = lowerBal.includes('portfolio') || lowerBal.includes('total worth') ||
                               lowerBal.includes('total value') || lowerBal.includes('how much am i worth') ||
                               lowerBal.includes('net worth') || lowerBal.includes('total balance');

        if ( wantsPortfolio ) {
            try {
                const res  = await fetch( 'https://api.coingecko.com/api/v3/simple/price?ids=ethereum,solana,binancecoin,matic-network,bitcoin&vs_currencies=usd' );
                const data = await res.json();
                const solBal   = parseFloat( balances?.solana   || 0 );
                const ethBal   = parseFloat( balances?.ethereum || balances?.eth || 0 );
                const bnbBal   = parseFloat( balances?.bsc      || 0 );
                const maticBal = parseFloat( balances?.polygon  || 0 );
                const btcBal   = parseFloat( balances?.bitcoin  || 0 );
                const total    = ( solBal * (data.solana?.usd||0) ) +
                                 ( ethBal * (data.ethereum?.usd||0) ) +
                                 ( bnbBal * (data.binancecoin?.usd||0) ) +
                                 ( maticBal * (data['matic-network']?.usd||0) ) +
                                 ( btcBal * (data.bitcoin?.usd||0) );
                const reply = 'Portfolio value: $' + total.toFixed(2) + ' USD — SOL: $' + (solBal*(data.solana?.usd||0)).toFixed(2) + ' | ETH: $' + (ethBal*(data.ethereum?.usd||0)).toFixed(2) + ' | BNB: $' + (bnbBal*(data.binancecoin?.usd||0)).toFixed(2);
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( 'Portfolio is $' + total.toFixed(2) + '.' );
            } catch(e) {
                const reply = 'Could not calculate portfolio value right now. Please check the Portfolio Tracker in Apps.';
                setMessages( prev => [ ...prev, { role: 'assistant', content: reply } ] );
                speak( reply );
            }
            setLoading( false );
            return;
        }

        /* send crypto */
        const sendResponse = handleSendIntent( text );
        if ( sendResponse ) {
            setMessages( prev => [ ...prev, { role: 'assistant', content: sendResponse } ] );
            speak( sendResponse );
            setLoading( false );
            return;
        }

        /* build wallet context */
        const allBalances = [];
        if ( balances ) {
            if ( balances.bitcoin  ) allBalances.push( `BTC: ${ balances.bitcoin }` );
            if ( balances.ethereum ) allBalances.push( `ETH: ${ balances.ethereum }` );
            if ( balances.bsc      ) allBalances.push( `BNB: ${ balances.bsc }` );
            if ( balances.polygon  ) allBalances.push( `MATIC: ${ balances.polygon }` );
            if ( balances.base     ) allBalances.push( `Base ETH: ${ balances.base }` );
            if ( balances.arbitrum ) allBalances.push( `ARB: ${ balances.arbitrum }` );
            if ( balances.solana   ) allBalances.push( `SOL: ${ balances.solana }` );
        }
        const balanceStr  = allBalances.length > 0 ? allBalances.join( ', ' ) : 'All balances are 0';
        const networkName = ( currentNetwork && currentNetwork.name ) || selectedNetwork || 'Unknown';

        /* get account count from localStorage */
        const storedAccounts = localStorage.getItem( 'chainvault_accounts' );
        const accountList    = storedAccounts ? JSON.parse( storedAccounts ) : [];
        const accountCount   = accountList.length;
        const activeIdx      = parseInt( localStorage.getItem( 'chainvault_active_account' ) || '0' );
        const walletTypeStr  = walletType === 'metamask' ? 'MetaMask' : walletType === 'phantom' ? 'Phantom' : 'Built-in wallet';

        /* build detailed account list for Rosie */
        const accountDetails = accountList.map( function(a) {
            return 'Account ' + (a.index + 1) + ' — ETH: ' + (a.ethereumAddress || 'N/A') + ' | SOL: ' + (a.solanaAddress || 'N/A');
        }).join(' | ');

        const context = {
            network:        networkName,
            address:        address || 'No address',
            walletStatus:   address ? 'CONNECTED using ' + walletTypeStr : 'Not connected',
            balances:       balanceStr,
            totalAccounts:  accountCount,
            activeAccount:  'Account ' + (activeIdx + 1),
            allAccounts:    accountCount > 0 ? accountDetails : 'No accounts found',
            canCreateMore:  'Yes - user can create Account ' + (accountCount + 1) + ' and beyond',
            page:           window.location.hash || '/',
            userName:       userNameRef.current || null,
        };

        try {
            const res  = await fetch( '/wp-json/chainvault/v1/rosie', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify( { messages: newMessages.slice( -10 ), context } ),
            } );
            const data = await res.json();
            if ( data.reply ) {
                setMessages( prev => [ ...prev, { role: 'assistant', content: data.reply } ] );
                speak( data.reply );
                if ( !open ) setUnread( u => u + 1 );
            } else {
                setMessages( prev => [ ...prev, { role: 'assistant', content: '⚠️ ' + ( data.error || 'Something went wrong.' ) } ] );
            }
        } catch {
            setMessages( prev => [ ...prev, { role: 'assistant', content: '⚠️ Could not connect. Check your internet.' } ] );
        }
        setLoading( false );
    }, [ loading, messages, balances, selectedNetwork, address, open, speak, awaitingSeedVerification, pendingSend, builtInWallet, switchNetwork, currentNetwork, autoListenForSeed, clearSeedTimers ] );

    /* greet user on onboarding screen */
    React.useEffect( () => {
        if ( showOnboarding && open ) {
            setTimeout( () => {
                const greeting = "👋 Hi! I'm Rosie, your ChainVault assistant. I can help you create your wallet right now — just say 'create wallet' or tap the mic and tell me you're ready to get started!";
                setMessages( [ { role: 'assistant', content: greeting } ] );
                speak( greeting );
            }, 1000 );
        }
    }, [ showOnboarding, open ] );

    /* always keep ref pointing to latest processMessage so voice callback never goes stale */
    const processMessageRef = React.useRef( processMessage );
    React.useEffect( () => { processMessageRef.current = processMessage; }, [ processMessage ] );

    const send = useCallback( () => {
        const text = input.trim();
        if ( !text ) return;
        setInput( '' );
        processMessage( text );
    }, [ input, processMessage ] );

    const onKey = ( e ) => {
        if ( e.key === 'Enter' && !e.shiftKey ) { e.preventDefault(); send(); }
    };

    const suggestions = [
        'What is my balance?',
        'Switch to Solana',
        'Open Exchange',
        'Create new wallet',
    ];

    /* Show Rosie only when user has any wallet connected */
    const hasBuiltIn  = builtInWallet && builtInWallet.mnemonic && builtInWallet.mnemonic.length > 0;
    const hasExternal = walletType === 'metamask' || walletType === 'phantom';
    const hasWallet   = hasBuiltIn || hasExternal;
    if ( !enabled ) return null;
    if ( !hasWallet && !showOnboarding ) return null;

    if ( fullPage ) {
        return (
            <div style={{ display:'flex',flexDirection:'column',height:'100%',background:'var(--cv-bg)' }}>
                <div style={{ flex:1,overflowY:'auto',padding:'12px',display:'flex',flexDirection:'column',gap:'10px',scrollbarWidth:'none' }}>
                    { messages.map( ( msg, i ) => (
                        <div key={i} style={{ display:'flex',justifyContent:msg.role==='user'?'flex-end':'flex-start',alignItems:'flex-end',gap:'6px' }}>
                            { msg.role === 'assistant' && <div style={{ width:'28px',height:'28px',borderRadius:'50%',background:'linear-gradient(135deg,#6C63FF,#FC72FF)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',flexShrink:0 }}>🤖</div> }
                            <div style={{ maxWidth:'78%',padding:'10px 14px',borderRadius:msg.role==='user'?'18px 18px 4px 18px':'18px 18px 18px 4px',background:msg.role==='user'?'linear-gradient(135deg,#6C63FF,#FC72FF)':'rgba(255,255,255,0.06)',color:'#fff',fontSize:'14px',lineHeight:'1.5',border:msg.role==='user'?'none':'1px solid rgba(255,255,255,0.08)' }}>{ msg.content }</div>
                        </div>
                    ))}
                    { loading && <div style={{ color:'#888',fontSize:'14px',padding:'4px 8px' }}>Rosie is thinking...</div> }
                    <div ref={ bottomRef } />
                </div>
                <div style={{ display:'flex',gap:'6px',padding:'6px 12px',overflowX:'auto',scrollbarWidth:'none',borderTop:'1px solid rgba(255,255,255,0.04)',flexShrink:0 }}>
                    { ['My balance','BTC price','Send SOL','Gas fees','I need support'].map((q,i) => (
                        <button key={i} onClick={()=>handleSendIntent(q)} style={{ flexShrink:0,background:'rgba(108,99,255,0.1)',border:'1px solid rgba(108,99,255,0.25)',borderRadius:'20px',padding:'6px 12px',color:'#a78bfa',fontSize:'12px',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap' }}>{ q }</button>
                    ))}
                </div>
                <div style={{ display:'flex',gap:'8px',padding:'8px 12px 12px',borderTop:'1px solid rgba(255,255,255,0.06)',flexShrink:0,alignItems:'center' }}>
                    <button onClick={ startListening } style={{ width:'42px',height:'42px',borderRadius:'50%',background:listening?'rgba(239,68,68,0.2)':'rgba(108,99,255,0.15)',border:listening?'2px solid #ef4444':'2px solid rgba(108,99,255,0.3)',color:listening?'#ef4444':'#a78bfa',fontSize:'20px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>🎙</button>
                    <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSendIntent(input)} placeholder="Ask Rosie anything..."
                        style={{ flex:1,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'22px',padding:'10px 16px',color:'#fff',fontSize:'14px',fontFamily:'inherit',outline:'none' }} />
                    <button onClick={()=>handleSendIntent(input)} style={{ width:'42px',height:'42px',borderRadius:'50%',background:'linear-gradient(135deg,#6C63FF,#FC72FF)',border:'none',color:'#fff',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>➤</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <audio ref={ audioRef } style={{ display:'none' }} />

            {/* Floating button */}
            <button
                onClick={ async () => {
                    if ( !micGranted ) {
                        try {
                            const stream = await navigator.mediaDevices.getUserMedia( { audio: true } );
                            stream.getTracks().forEach( t => t.stop() );
                            setMicGranted( true );
                        } catch(e) {
                            /* still denied — open Rosie anyway, typing still works */
                            console.log( 'Mic still denied:', e.message );
                        }
                    }
                    setOpen( o => !o );
                } }
                style={{
                    position:'fixed', bottom:24, right:24, width:56, height:56, borderRadius:'50%',
                    background: speaking ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#FC72FF,#818cf8)',
                    border:'none', cursor:'pointer', zIndex:99999,
                    boxShadow: listening ? '0 0 0 8px rgba(239,68,68,0.3), 0 4px 20px rgba(252,114,255,0.4)' : '0 4px 20px rgba(252,114,255,0.4)',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:22,
                    animation: listening ? 'rosie-pulse 1s ease-in-out infinite' : 'none',
                    transition: 'all 0.3s',
                    opacity: 1,
                }}
                title={ `Chat with ${ rosieName }` }
            >
                <style>{`
                    @keyframes rosie-pulse {
                        0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.5), 0 4px 20px rgba(252,114,255,0.4); }
                        70%  { box-shadow: 0 0 0 14px rgba(239,68,68,0), 0 4px 20px rgba(252,114,255,0.4); }
                        100% { box-shadow: 0 0 0 0 rgba(239,68,68,0), 0 4px 20px rgba(252,114,255,0.4); }
                    }
                `}</style>
                { open ? '✕' : speaking ? '🔊' : '🤖' }
                { !open && unread > 0 && (
                    <span style={{ position:'absolute', top:-4, right:-4, background:'#ef4444', color:'#fff', borderRadius:'50%', width:18, height:18, fontSize:10, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>
                        { unread }
                    </span>
                )}
            </button>

            {/* Chat window */}
            { open && (
                <div style={{
                    position:'fixed', bottom:90, right:24, width:370, maxHeight:540,
                    background:'#0e0e18', border:'1px solid rgba(255,255,255,0.08)',
                    borderRadius:16, zIndex:99998, display:'flex', flexDirection:'column',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.6)', fontFamily:'inherit', overflow:'hidden',
                }}>
                    {/* Header */}
                    <div style={{ padding:'14px 16px', background:'linear-gradient(135deg,rgba(252,114,255,0.15),rgba(129,140,248,0.15))', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                        <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#FC72FF,#818cf8)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, animation: speaking ? 'rosie-pulse 1s ease-in-out infinite' : 'none' }}>
                            { speaking ? '🔊' : '🤖' }
                        </div>
                        <div>
                            <div style={{ fontWeight:700, color:'#fff', fontSize:14 }}>{ rosieName }</div>
                            <div style={{ fontSize:11, color: speaking ? '#FC72FF' : listening ? '#ef4444' : '#22c55e' }}>
                                { speaking ? '● Speaking...' : listening ? '● Listening...' : '● Online' }
                            </div>
                        </div>
                        <div style={{ marginLeft:'auto', fontSize:11, color:'#555' }}>
                            { ( currentNetwork && currentNetwork.name ) || selectedNetwork }
                        </div>
                        <button onClick={ () => setOpen(false) }
                            style={{ background:'transparent', border:'none', color:'#555', cursor:'pointer', fontSize:18, padding:'4px 8px' }}>✕</button>
                    </div>

                    {/* Messages */}
                    <div style={{ flex:1, overflowY:'auto', padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
                        { messages.map( ( msg, i ) => (
                            <div key={i} style={{ display:'flex', justifyContent: msg.role==='user'?'flex-end':'flex-start' }}>
                                <div style={{
                                    maxWidth:'80%', padding:'9px 13px',
                                    borderRadius: msg.role==='user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                    background: msg.role==='user' ? 'linear-gradient(135deg,#FC72FF,#818cf8)' : 'rgba(255,255,255,0.07)',
                                    color:'#fff', fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap', wordBreak:'break-word',
                                }}>
                                    { msg.content }
                                </div>
                            </div>
                        ))}

                        {/* Interim speech text — like Siri showing what it hears */}
                        { interimText && (
                            <div style={{ display:'flex', justifyContent:'flex-end' }}>
                                <div style={{ maxWidth:'80%', padding:'9px 13px', borderRadius:'14px 14px 4px 14px', background:'rgba(252,114,255,0.15)', border:'1px dashed rgba(252,114,255,0.4)', color:'#FC72FF', fontSize:13, fontStyle:'italic' }}>
                                    { interimText }...
                                </div>
                            </div>
                        )}

                        { loading && (
                            <div style={{ display:'flex', justifyContent:'flex-start' }}>
                                <div style={{ padding:'10px 14px', background:'rgba(255,255,255,0.07)', borderRadius:'14px 14px 14px 4px', color:'#888', fontSize:20, letterSpacing:4 }}>···</div>
                            </div>
                        )}
                        <div ref={ bottomRef } />
                    </div>

                    {/* Suggestions */}
                    { messages.length === 1 && (
                        <div style={{ padding:'0 12px 10px', display:'flex', flexWrap:'wrap', gap:6, flexShrink:0 }}>
                            { suggestions.map( ( s, i ) => (
                                <button key={i} onClick={ () => processMessage(s) }
                                    style={{ padding:'5px 10px', background:'rgba(252,114,255,0.1)', border:'1px solid rgba(252,114,255,0.25)', borderRadius:20, color:'#FC72FF', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
                                    { s }
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:8, flexShrink:0, background:'#0a0a10' }}>
                        <button
                            onClick={ startListening }
                            title={ listening ? 'Stop listening' : 'Tap to speak' }
                            style={{
                                width:44, height:44, borderRadius:'50%', flexShrink:0,
                                background: listening ? 'rgba(239,68,68,0.3)' : 'linear-gradient(135deg,rgba(252,114,255,0.3),rgba(129,140,248,0.3))',
                                border:`2px solid ${ listening ? '#ef4444' : 'rgba(252,114,255,0.5)' }`,
                                cursor:'pointer', fontSize:20, display:'flex', alignItems:'center', justifyContent:'center',
                                animation: listening ? 'rosie-pulse 1s ease-in-out infinite' : 'none',
                                transition:'all 0.2s',
                                boxShadow: listening ? '0 0 12px rgba(239,68,68,0.4)' : '0 0 8px rgba(252,114,255,0.3)',
                            }}>
                            { listening ? '🔴' : '🎤' }
                        </button>
                        <textarea
                            ref={ inputRef }
                            value={ input }
                            onChange={ e => setInput( e.target.value ) }
                            onKeyDown={ onKey }
                            placeholder={ listening ? '🎤 Listening...' : `Ask ${ rosieName } anything...` }
                            rows={1}
                            style={{ flex:1, padding:'9px 12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, color:'#fff', fontFamily:'inherit', fontSize:13, outline:'none', resize:'none', lineHeight:1.4 }}
                        />
                        <button onClick={ send } disabled={ loading || !input.trim() }
                            style={{ padding:'9px 14px', background: loading||!input.trim() ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg,#FC72FF,#818cf8)', border:'none', borderRadius:10, color: loading||!input.trim()?'#444':'#fff', cursor: loading||!input.trim()?'not-allowed':'pointer', fontSize:16, flexShrink:0 }}>
                            ➤
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
